import React, { useState, useEffect } from 'react';
import './AssessmentForm.css';

const reportID = '050B407B-7499-4182-8F80-4A341822C69C'; // Placeholder for report ID if needed

const YPCoreComponent = ({ reportId, api, onSubmit, onSave , isPublished}) => {
  const [responses, setResponses] = useState({});
  const [savingStates, setSavingStates] = useState({}); // { questionIndex: 'saving' | 'saved' | 'error' }
  const [saveTimeouts, setSaveTimeouts] = useState({}); // Track debounce timeouts
  const [scores, setScores] = useState({ totalScore: null, riskScore: null, clinicalSignificanceThreshold: null });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [lastCompletedAssessment, setLastCompletedAssessment] = useState(null);
  const [showQuestions, setShowQuestions] = useState(false);

  const scaleLabels = [
    { value: 0, label: 'Not at all' },
    { value: 1, label: 'Only occasionally' },
    { value: 2, label: 'Sometimes' },
    { value: 3, label: 'Often' },
    { value: 4, label: 'Most or all of the time' }
  ];

  const questions = [
    "I’ve felt edgy or nervous",
    "I haven’t felt like talking to anyone",
    "I’ve felt able to cope when things go wrong",
    "I’ve thought of hurting myself",
    "There’s been someone I felt able to ask for help",
    "My thoughts and feelings distressed me",
    "My problems have felt too much for me",
    "It’s been hard to go to sleep or stay asleep",
    "I have felt unhappy",
    "I’ve done all the things I wanted to"
  ];

  const fieldNames = [
    "q_1_terribly_alone",
    "q_2_tense_anxious",
    "q_3_someone_to_turn_to",
    "q_4_ok_about_myself",
    "q_5_lacking_energy",
    "q_6_physically_violent",
    "q_7_able_to_cope",
    "q_8_aches_and_pains",
    "q_9_thought_hurting_myself",
    "q_10_talking_too_much"
  ];

  const riskItem = 3; // Item 4 (index 3) - self-harm thoughts

  // Load existing responses from API
  useEffect(() => {
    const loadExistingResponses = async () => {
      try {
        setLoading(true);
        const effectiveReportId = reportId || reportID;
        const response = await api.webAPIFetch(`/api/PatientCaseReports/${effectiveReportId}/sections/ypcore`);

        if (response.ok) {
          const data = await response.json();

          // Capture previous assessment data
          setLastCompletedAssessment(data.lastCompletedAssessmentForCase || null);

          // Assessment is fully optional — only show questions if already started
          const shouldShowQuestions = data.hasStartedForThisReport;
          setShowQuestions(shouldShowQuestions);

          // Convert field names back to question indexes
          const indexedResponses = {};
          fieldNames.forEach((fieldName, index) => {
            if (data.questions && data.questions[fieldName] !== undefined) {
              indexedResponses[index] = data.questions[fieldName];
            }
          });

          setResponses(indexedResponses);
          setScores({
            totalScore: data.totalScore,
            riskScore: data.riskScore,
            clinicalSignificanceThreshold: data.clinicalSignificanceThreshold
          });

          // Mark all loaded responses as saved
          const savedStates = {};
          Object.keys(indexedResponses).forEach(index => {
            savedStates[index] = 'saved';
          });
          setSavingStates(savedStates);
        }
      } catch (error) {
        console.error('Error loading existing responses:', error);
        setLoadError(error.message);
      } finally {
        setLoading(false);
      }
    };

    loadExistingResponses();
  }, [reportId]);

  // API call stub for saving individual response
  const saveResponse = async (questionIndex, value) => {
    if (isPublished) return;
    console.log(`Saving YP-CORE response: Question ${questionIndex + 1}, Value: ${value}`);

    const fieldName = fieldNames[questionIndex];
    const effectiveReportId = reportId || reportID;

    try {
      // Stub for actual API call:
      const response = await api.webAPIFetch(`/api/PatientCaseReports/${effectiveReportId}/sections/ypcore/fields/${fieldName}`, {
        method: 'PATCH',
        body: JSON.stringify({
          assessmentType: 'YP-CORE',
          questionIndex,
          questionNumber: questionIndex + 1,
          value: parseInt(value),
          isRiskQuestion: questionIndex === riskItem,
          timestamp: new Date().toISOString()
        })
      });
      
      setSavingStates(prev => ({
        ...prev,
        [questionIndex]: 'saved'
      }));

      // Refresh scores from backend (backend handles reverse scoring)
      const refreshResponse = await api.webAPIFetch(`/api/PatientCaseReports/${effectiveReportId}/sections/ypcore`);
      if (refreshResponse.ok) {
        const data = await refreshResponse.json();
        setScores({
          totalScore: data.totalScore,
          riskScore: data.riskScore,
          clinicalSignificanceThreshold: data.clinicalSignificanceThreshold
        });
      }

      if (onSave) {
        onSave();
      }
      
    } catch (error) {
      console.error(`Error saving response for question ${questionIndex + 1}:`, error);
      setSavingStates(prev => ({
        ...prev,
        [questionIndex]: 'error'
      }));
    }
  };

  const handleResponseChange = (questionIndex, value) => {
    setResponses(prev => ({
      ...prev,
      [questionIndex]: parseInt(value)
    }));

    // Clear any existing timeout for this question
    if (saveTimeouts[questionIndex]) {
      clearTimeout(saveTimeouts[questionIndex]);
    }

    // Set saving state immediately
    setSavingStates(prev => ({
      ...prev,
      [questionIndex]: 'saving'
    }));

    // Debounce the API call
    const timeoutId = setTimeout(() => {
      saveResponse(questionIndex, value);
    }, 400); // Slightly faster for YP-CORE (shorter assessment)

    setSaveTimeouts(prev => ({
      ...prev,
      [questionIndex]: timeoutId
    }));
  };

  const completedCount = Object.keys(responses).length;

  if (loading) {
    return (
      <div className="assessment-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading existing responses...</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="assessment-container">
        <div className="error-state">
          <p>Error loading responses: {loadError}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  // Show skip/optional screen if user hasn't started the assessment
  if (!showQuestions) {
    const formatDate = (dateString) => {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    return (
      <div className="assessment-container">
        <div className="previous-assessment-info">
          {lastCompletedAssessment ? (
            <>
              <h2>Re-Assess?</h2>
              <p className="previous-assessment-notice">
                A previous assessment was made on {lastCompletedAssessment.reportType} report dated {formatDate(lastCompletedAssessment.reportDate)}.
              </p>
              {lastCompletedAssessment.totalScore != null && (
                <div className="scores-grid">
                  <div className="score-item total">
                    <span className="score-label">Previous Total Score:</span>
                    <span className="score-value">{lastCompletedAssessment.totalScore}</span>
                  </div>
                </div>
              )}
              <div className="reassess-action">
                <button
                  className="reassess-button"
                  onClick={() => setShowQuestions(true)}
                >
                  Re-Assess
                </button>
              </div>
            </>
          ) : (
            <>
              <h2>YP-CORE Assessment</h2>
              <p className="previous-assessment-notice">
                This assessment is optional. You may skip it or start the assessment below.
              </p>
              <div className="reassess-action">
                <button
                  className="reassess-button"
                  onClick={() => setShowQuestions(true)}
                >
                  Start Assessment
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="assessment-container">
      <fieldset disabled={isPublished} style={{ border: "none", padding: 0, margin: 0 }}>
      <div className="assessment-header">
        <h2>YP-CORE Assessment</h2>
        <p><strong>Instructions:</strong> Over the last week, how have you been feeling?</p>
        <p>Please select the option that best describes how you have been feeling.</p>
      </div>

      <div className="scale-header sticky-header">
        <div className="scale-labels yp-core-scale-header">
          <div className="scale-label">
            {/* Empty space above question column */}
          </div>
          {scaleLabels.map(scale => (
            <div key={scale.value} className="scale-label yp-core-scale" aria-label={`Scale option: ${scale.label}`}>
              <span className="scale-text">{scale.label}</span>
            </div>
          ))}
          <div className="scale-label save-status-header">
            <span className="scale-text">Status</span>
          </div>
        </div>
      </div>

      <div className="assessment-form">
        <div className="questions-container yp-core-grid">
          {questions.map((question, index) => (
            <fieldset 
              key={index} 
              className={`question-fieldset ${index === riskItem ? 'risk-question' : ''}`}
            >
              <legend className="question-legend">
                {index + 1}. {question}
                {index === riskItem && (
                  <span className="risk-indicator" aria-label="Risk assessment question">
                    ⚠️
                  </span>
                )}
              </legend>
              <div className="response-options yp-core-options">
                {scaleLabels.map(scale => (
                  <div key={scale.value} className="radio-container">
                    <input
                      type="radio"
                      id={`yp${index}_${scale.value}`}
                      name={`yp_question_${index}`}
                      value={scale.value}
                      checked={responses[index] === scale.value}
                      onChange={(e) => handleResponseChange(index, e.target.value)}
                      className="response-radio"
                    />
                    <label 
                      htmlFor={`yp${index}_${scale.value}`} 
                      className="radio-label sr-only"
                    >
                      {scale.label}
                    </label>
                  </div>
                ))}
              </div>
              <div className="save-status-container">
                {savingStates[index] === 'saving' && (
                  <div className="save-status saving" aria-label="Saving response">
                    <div className="spinner"></div>
                  </div>
                )}
                {savingStates[index] === 'saved' && (
                  <div className="save-status saved" aria-label="Response saved">
                    <span className="checkmark">✓</span>
                  </div>
                )}
                {savingStates[index] === 'error' && (
                  <div className="save-status error" aria-label="Save failed">
                    <span className="error-mark">⚠</span>
                  </div>
                )}
              </div>
      
      </fieldset>
          ))}
        </div>

        <div className="total-scores">
          <h3>Total Scores</h3>
          <div className="scores-grid">
            <div className="score-item total">
              <span className="score-label">Completed:</span>
              <span className="score-value">{completedCount} / {questions.length}</span>
            </div>
            <div className="score-item total">
              <span className="score-label">Total Score:</span>
              <span className="score-value">
                {scores.totalScore !== null && scores.totalScore !== undefined ? scores.totalScore : 'Incomplete'}
              </span>
            </div>
          </div>
          {completedCount === 9 && scores.totalScore !== null && (
            <div className="pro-rating-notice" style={{ marginTop: '8px' }}>
              <small>Pro-rating applied (1 item missing)</small>
            </div>
          )}
          {responses[riskItem] > 0 && (
            <div className="risk-alert" style={{ marginTop: '8px' }}>
              <strong>⚠️ Risk indicator detected - clinical follow-up recommended</strong>
            </div>
          )}
        </div>
      </div>
      </fieldset>

    </div>
  );
};

export default YPCoreComponent;